import { Grid, Table, TableCell, TableHead, TableRow, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import InfoIcon from '@material-ui/icons/Info'
import WarningIcon from '@material-ui/icons/Warning'
import { Alert } from '@material-ui/lab'
import { keyBy } from 'lodash'
import { useState } from 'react'
import {
  Datagrid,
  DateField,
  DateInput,
  FilterForm,
  FunctionField,
  ListContextProvider,
  Loading,
  ReferenceInput,
  required,
  SelectArrayInput,
  SelectInput,
  TextField,
  TextInput,
  useQuery,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { DocumentRowInfoStatus } from 'shared/components/Documents/DocumentRowInfoStatus'

import { createDocument, deleteDocument, updateDocument } from 'api/core'
import { BaseUploadButton } from 'components/Common/UploadButton'
import { getCountryFromCode } from 'helpers/countries'
import { getStateChoices } from 'helpers/states'
import {
  FolderName,
  getFileAttachmentOnDocument,
  getFolderName,
  GLOBAL_DOCUMENT,
  GLOBAL_DOCUMENT_CATEGORY_CHOICES,
} from '../../helpers/documents'
import { futureDateValidator } from '../../helpers/validators'
import { Document } from '../../models/document'

const useStyles = makeStyles({
  updateButton: {
    textTransform: 'none',
    visibility: 'hidden',
  },
  infoTop: {
    marginBottom: '20px',
  },
  infoBottom: {
    backgroundColor: 'white',
    width: '100%',
    padding: '20px',
    marginTop: '20px',
  },
  filterInput: {
    width: '250px',
    minWidth: '150px',
  },
  infoIcon: {
    marginRight: '16px',
  },
  loading: {
    width: '100%',
  },
  tableColumnSmall: {
    width: '15%',
  },
  noDocumentUploadedText: {
    height: '200px',
    color: 'gray',
    display: 'flex',
    alignItems: 'center',
  },
  emptyContainer: {
    backgroundColor: 'white',
  },
  emptyTable: {
    width: '100%',
  },
  iconInfo: {
    marginRight: '5px',
  },
  boldFont: {
    fontWeight: 'bold',
  },
  rowHover: {
    '& tbody tr:hover': {
      '& td .updateButton': {
        visibility: 'visible',
      },
    },
  },
  warningCallout: {
    backgroundColor: '#FFF3C8',
    padding: '16px 50px',
    margin: '20px 0',
  },
  warningIconContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '32px',
    height: '32px',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginRight: '5px',
  },
  warningText: {
    marginBottom: '10px',
  },
})

const rowStyle = () => ({
  height: '70px',
})

const getDocumentFilters = (classes: any) => {
  return [
    <SelectArrayInput
      alwaysOn
      label="Filter by Category"
      source="folder_name"
      choices={GLOBAL_DOCUMENT_CATEGORY_CHOICES}
      className={classes?.filterInput}
    />,
  ]
}

const CarrierReferenceInput = (props: any) => {
  return (
    <ReferenceInput {...props}>
      <SelectInput optionText="name" />
    </ReferenceInput>
  )
}

const getFormData = (props: any, record?: Document) => {
  const country: any = getCountryFromCode('US')
  const stateChoices = getStateChoices(country.states(true))
  const formDataInputs = [
    {
      name: 'document_name',
      input: TextInput,
      validate: required(),
      value: record?.document_name,
      label: 'Document Title',
      size: 10,
      isTop: true,
    },
    {
      name: 'folder_name',
      input: SelectInput,
      choices: GLOBAL_DOCUMENT_CATEGORY_CHOICES,
      validate: required(),
      value: record?.folder_name,
      label: 'Category',
      size: 10,
      isTop: true,
    },
    {
      name: 'date_expired',
      input: DateInput,
      choices: undefined,
      validate: [futureDateValidator],
      value: record?.date_expired,
      label: 'Date Expired',
      size: 10,
      isTop: false,
    },
    {
      name: 'permit_state',
      input: SelectInput,
      choices: stateChoices,
      validate: required(),
      value: record?.metadata?.permit_state,
      label: 'State',
      size: 10,
      isTop: false,
      condition: (values: any) => values.folder_name === FolderName.ALCOHOL_PERMITS,
    },
    {
      name: 'carrier.id',
      input: CarrierReferenceInput,
      label: 'Carrier',
      validate: required(),
      value: record?.carrier?.id,
      reference: 'carriers',
    },
  ]

  return formDataInputs
}

function updateFormDataWithMetadata(formData: any) {
  const foldername = formData.get('folder_name')
  if (foldername === FolderName.ALCOHOL_PERMITS) {
    const metadata = {
      permit_state: formData.get('permit_state'),
    }
    formData.append('metadata', JSON.stringify(metadata))
  }
  const carrier_id = formData.get('carrier.id')
  if (carrier_id) {
    formData.append('carrier_id', carrier_id)
  }
  return formData
}

const updateComplianceDocument = async (id: any, formData: any) => {
  formData = updateFormDataWithMetadata(formData)
  return await updateDocument(id, formData)
}

const handleDelete = async (id: any) => {
  return await deleteDocument(id)
}

const UpdateDocumentButton = (props: any) => {
  const { record, classes } = props
  const isUpdate = !!record.id
  return (
    <BaseUploadButton
      record={record}
      uploadFunction={updateComplianceDocument}
      fileFieldInputs={[
        {
          name: GLOBAL_DOCUMENT,
          label: 'Global Document',
          value: getFileAttachmentOnDocument(record.document_attachment_versions[0][0]),
        },
      ]}
      dialogTitle="Update CT-Provided Document"
      buttonLabel="Update"
      formDataInputs={getFormData(props, record)}
      additionalPayloadData={{
        document_type: record.document_type,
        is_update: isUpdate,
      }}
      buttonVariant="text"
      multiple={false}
      buttonClassNames={classes.updateButton + ' updateButton'}
      deleteFunction={isUpdate ? handleDelete : undefined}
      Warning={
        <WarningCallout
          classes={classes}
          text="This document will be shown in the permit book section of the mobile app."
        />
      }
    />
  )
}

const EmptyData = ({ classes }: any) => {
  return (
    <Grid item container alignItems="center" justifyContent="center" className={classes.emptyContainer}>
      <Table className={classes.emptyTable}>
        <TableHead>
          <TableRow>
            <TableCell>Document</TableCell>
            <TableCell className={classes.tableColumnSmall}>Document Status</TableCell>
            <TableCell className={classes.tableColumnSmall}>Expiration Date</TableCell>
            <TableCell>Category</TableCell>
          </TableRow>
        </TableHead>
      </Table>
      <Typography className={classes.noDocumentUploadedText}>No document uploaded yet</Typography>
    </Grid>
  )
}

const WarningCallout = ({ text, classes }: any) => {
  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      className={classes.warningCallout}
      direction="row"
      spacing={2}
    >
      <Grid item className={classes.warningIconContainer}>
        <WarningIcon fontSize="large" style={{ color: 'orange', fontSize: 'x-large' }} />
      </Grid>
      <Grid item xs={10}>
        <Typography variant={'body1'} component="span">
          {text}
        </Typography>
      </Grid>
    </Grid>
  )
}

const createComplianceDocument = async (id: any, formData: any) => {
  formData = updateFormDataWithMetadata(formData)
  return await createDocument(id, formData)
}

const ComplianceDocumentsList = (props: any) => {
  const { classes } = props
  const [filter, setFilter] = useState('')
  const [page, setPage] = useState(1)

  const handleRowClick = (id: any, basePath: any, record: any): string => {
    const file = getFileAttachmentOnDocument(
      record?.document_attachment_versions ? record?.document_attachment_versions[0][0] : undefined
    )
    if (file) {
      window.open(file)
    }
    return ''
  }

  const perPage = 50
  const currentSort = { field: 'id', order: 'ASC' }
  const filterValues = filter ? { folder_name: filter } : {}
  const { data, total, loading, error } = useQuery({
    type: 'getList',
    resource: 'documents/all-global',
    payload: {
      pagination: { page, perPage },
      sort: currentSort,
      filter: filterValues,
    },
  })

  const ids = data?.map(({ id }: any) => id)
  const setFilters = (filters: any) => {
    setFilter(filters.folder_name)
  }
  const context = {
    data: keyBy(data, 'id'),
    total,
    page,
    perPage,
    setPage,
    currentSort,
    filterValues,
    setFilters,
    loading: loading,
    loaded: !!data,
    selectedIds: [],
    basePath: 'documents/all-global',
    resource: 'documents',
    ids,
  }
  return loading ? (
    <Grid item container direction="row">
      <Loading className={classes.loading} />
    </Grid>
  ) : error ? (
    <Grid item container direction="row">
      <Alert severity="error">{error}</Alert>
    </Grid>
  ) : data ? (
    <ListContextProvider value={context}>
      <Grid item container direction="row" justifyContent="space-between" spacing={2}>
        <Grid item container xs={12} sm={6} direction="row" justifyContent="flex-start" alignItems="center">
          <FilterForm filters={getDocumentFilters(classes)} />
          <Typography variant="subtitle2" component="div" className={classes.boldFont}>
            {total} documents
          </Typography>
        </Grid>
        <Grid item container xs={12} sm={6} justifyContent="flex-end">
          <BaseUploadButton
            uploadFunction={createComplianceDocument}
            fileFieldInputs={[
              {
                name: GLOBAL_DOCUMENT,
                label: 'Global Document',
              },
            ]}
            dialogTitle="Add CT-Provided Document"
            buttonLabel="Add New Document"
            formDataInputs={getFormData(props)}
            additionalPayloadData={{
              document_type: GLOBAL_DOCUMENT,
              is_update: false,
            }}
            buttonVariant="text"
            buttonIcon={<AddCircleOutlineIcon />}
            multiple={false}
            Warning={
              <WarningCallout
                classes={classes}
                text="This document will be shown in the permit book section of the CT app of all Virtual Carrier drivers and admins."
              />
            }
          />
        </Grid>
      </Grid>
      <Datagrid
        optimized
        rowStyle={rowStyle}
        rowClick={handleRowClick}
        empty={<EmptyData classes={classes} />}
        className={classes.rowHover}
      >
        <TextField label="Document" source="document_name" />
        <FunctionField
          label="Document Status"
          render={(record: any) => {
            if (record.document_attachment_versions.length === 0) {
              // return not uploaded
              return <DocumentRowInfoStatus item={null} />
            }
            return record.document_attachment_versions[0].map((version: any, i: number) => (
              <FlexBox key={i}>
                <DocumentRowInfoStatus item={version} />
              </FlexBox>
            ))
          }}
        />
        <DateField
          label="Expiration Date"
          source="date_expired"
          emptyText="-"
          options={{ timeZoneName: 'short' }}
        />
        <FunctionField label="Category" render={(record: any) => getFolderName(record)} />
        <TextField label="CT MC" source="carrier.name" />
        <UpdateDocumentButton classes={classes} {...props} />
      </Datagrid>
    </ListContextProvider>
  ) : null
}

const ComplianceDocumentsManagement = () => {
  const classes = useStyles()

  return (
    <div>
      <Grid item container>
        <Grid item container className={classes.infoTop}>
          <Typography variant="h5" gutterBottom component="div">
            Configure Compliance Documents
          </Typography>
          <Typography variant="body1" gutterBottom>
            CT staff can add new CT-owned documents and update the existing ones here. Documents in this list
            will be displayed to the users in the Permit Book section of the mobile app.
          </Typography>
        </Grid>
        <ComplianceDocumentsList classes={classes} />
        <Grid item container className={classes.infoBottom}>
          <Grid item container alignItems="center">
            <InfoIcon className={classes.iconInfo} color="primary" />
            <Typography variant="h6" component="div">
              Fleet and Driver Specific Documents
            </Typography>
          </Grid>
          <Typography variant="body1" gutterBottom>
            CT app will display these documents in the Permit book as well. These documents are mostly
            provided by the fleet and CT Staff does not maintain them here.
            <br />
            <br />
            &bull; CDL
            <br />
            &bull; Medical Certificate
            <br />
            &bull; Driver Occupational Accident Insurance
            <br />
            &bull; Truck Registration (Cab Card)
            <br />
            &bull; Truck & Trailer Insurance: Non-Trucking Liability
            <br />
            &bull; IFTA Permit (maintained by CT if a truck is marked as CT-IFTA)
            <br />
            &bull; Trailer Registration (Cab Card)
            <br />
            &bull; Trailer Inspection
            <br />
            &bull; Physical Damage Insurance (optional)
            <br />
          </Typography>
        </Grid>
      </Grid>
    </div>
  )
}

export default ComplianceDocumentsManagement
